(() => {
  const IMAGE_SELECTOR =
    '.article-template__content img:not([data-no-zoom])';

  const DRAG_THRESHOLD = 5;

  let dialog;
  let viewport;
  let modalImage;
  let closeButton;
  let sourceImage;

  let isDragging = false;
  let didDrag = false;
  let activePointerId = null;

  let dragStartX = 0;
  let dragStartY = 0;
  let scrollStartLeft = 0;
  let scrollStartTop = 0;

  /*
   * URLが画像ファイルかどうかを確認
   */
  const isImageUrl = (url) => {
    return /\.(avif|webp|png|jpe?g|gif)(?:[?#].*)?$/i.test(url || '');
  };

  /*
   * srcset内で最も大きい画像URLを取得
   */
  const getLargestSrcsetUrl = (image) => {
    const srcset = image.getAttribute('srcset');

    if (!srcset) return '';

    const candidates = srcset
      .split(',')
      .map((candidate) => {
        const parts = candidate.trim().split(/\s+/);
        const url = parts[0] || '';
        const width = parseInt(parts[1], 10) || 0;

        return {
          url,
          width
        };
      })
      .filter((candidate) => candidate.url);

    candidates.sort((a, b) => b.width - a.width);

    return candidates[0]?.url || '';
  };

  /*
   * Shopify CDNの縮小指定を除去し、
   * 可能な限り元画像のURLを取得
   */
  const getOriginalImageUrl = (image) => {
    const linkedUrl = image.closest('a')?.href;

    let rawUrl = '';

    if (linkedUrl && isImageUrl(linkedUrl)) {
      rawUrl = linkedUrl;
    } else {
      rawUrl =
        getLargestSrcsetUrl(image) ||
        image.getAttribute('src') ||
        image.currentSrc ||
        image.src;
    }

    if (
      !rawUrl ||
      rawUrl.startsWith('data:') ||
      rawUrl.startsWith('blob:')
    ) {
      return rawUrl;
    }

    try {
      const url = new URL(rawUrl, window.location.href);

      [
        'width',
        'height',
        'crop',
        'crop_left',
        'crop_top',
        'crop_width',
        'crop_height',
        'quality'
      ].forEach((parameter) => {
        url.searchParams.delete(parameter);
      });

      /*
       * 古いShopify画像URLの
       * _1024x、_largeなども除去
       */
      url.pathname = url.pathname.replace(
        /_(pico|icon|thumb|small|compact|medium|large|grande|original|master|\d+x\d*|x\d+)(?=\.[a-z0-9]+$)/i,
        ''
      );

      return url.toString();
    } catch (error) {
      return rawUrl;
    }
  };

  /*
   * ドラッグ状態を解除
   */
  const resetDragging = () => {
    isDragging = false;
    activePointerId = null;

    if (viewport) {
      viewport.classList.remove('is-dragging');
    }
  };

  /*
   * 原寸画像の中央付近を表示
   */
  const centerZoomedImage = () => {
    requestAnimationFrame(() => {
      if (!viewport || !modalImage) return;

      viewport.scrollLeft = Math.max(
        0,
        (viewport.scrollWidth - viewport.clientWidth) / 2
      );

      viewport.scrollTop = Math.max(
        0,
        (viewport.scrollHeight - viewport.clientHeight) / 2
      );
    });
  };

  /*
   * 拡大画面を閉じる
   */
  const closeLightbox = () => {
    if (dialog?.open) {
      dialog.close();
    }
  };

  /*
   * 画像を原寸表示に切り替える
   */
 const zoomToOriginalSize = () => {
  if (!dialog || dialog.classList.contains('is-zoomed')) {
    return;
  }

  const applyZoomSize = () => {
    const isLandscape =
      modalImage.naturalWidth >= modalImage.naturalHeight;

    const zoomWidth = isLandscape
      ? modalImage.naturalWidth * 1.4
      : modalImage.naturalWidth;

    modalImage.style.setProperty(
      '--article-zoom-width',
      `${zoomWidth}px`
    );

    dialog.classList.add('is-zoomed');
    centerZoomedImage();
  };

  if (modalImage.complete && modalImage.naturalWidth > 0) {
    applyZoomSize();
  } else {
    modalImage.addEventListener(
      'load',
      applyZoomSize,
      { once: true }
    );
  }
};

  /*
   * 拡大画面を生成
   */
  const createLightbox = () => {
    if (dialog) return;

    dialog = document.createElement('dialog');
    dialog.className = 'article-lightbox';
    dialog.setAttribute('aria-label', '画像拡大表示');

    dialog.innerHTML = `
      <button
        class="article-lightbox__close"
        type="button"
        aria-label="拡大画像を閉じる"
      >
        Close ×
      </button>

      <div class="article-lightbox__viewport">
        <img
          class="article-lightbox__image"
          alt=""
          draggable="false"
        >
      </div>
    `;

    document.body.appendChild(dialog);

    viewport = dialog.querySelector('.article-lightbox__viewport');
    modalImage = dialog.querySelector('.article-lightbox__image');
    closeButton = dialog.querySelector('.article-lightbox__close');

    /*
     * Closeボタン
     */
    closeButton.addEventListener('click', closeLightbox);

    /*
     * 画像をクリック
     *
     * 最初のクリック：
     * 画面内表示から原寸表示へ切り替える
     *
     * 原寸表示中：
     * クリックしても何も起こらない
     */
    modalImage.addEventListener('click', (event) => {
      event.stopPropagation();

      if (didDrag) {
        didDrag = false;
        return;
      }

      if (!dialog.classList.contains('is-zoomed')) {
        zoomToOriginalSize();
      }
    });

    /*
     * 画像の外側を左クリックすると閉じる
     */
    viewport.addEventListener('click', (event) => {
      if (didDrag) {
        didDrag = false;
        return;
      }

      if (event.target === viewport) {
        closeLightbox();
      }
    });

    /*
     * 原寸表示中の左ドラッグ開始
     */
    viewport.addEventListener('pointerdown', (event) => {
      if (
        !dialog.classList.contains('is-zoomed') ||
        event.button !== 0 ||
        event.target !== modalImage
      ) {
        return;
      }

      isDragging = true;
      didDrag = false;
      activePointerId = event.pointerId;

      dragStartX = event.clientX;
      dragStartY = event.clientY;

      scrollStartLeft = viewport.scrollLeft;
      scrollStartTop = viewport.scrollTop;

      viewport.classList.add('is-dragging');
      viewport.setPointerCapture(event.pointerId);

      event.preventDefault();
    });

    /*
     * 左ドラッグで画像内を移動
     */
    viewport.addEventListener('pointermove', (event) => {
      if (
        !isDragging ||
        event.pointerId !== activePointerId
      ) {
        return;
      }

      const moveX = event.clientX - dragStartX;
      const moveY = event.clientY - dragStartY;

      if (
        Math.abs(moveX) > DRAG_THRESHOLD ||
        Math.abs(moveY) > DRAG_THRESHOLD
      ) {
        didDrag = true;
      }

      viewport.scrollLeft = scrollStartLeft - moveX;
      viewport.scrollTop = scrollStartTop - moveY;

      event.preventDefault();
    });

    /*
     * 左ドラッグ終了
     */
    const stopDragging = (event) => {
      if (
        !isDragging ||
        event.pointerId !== activePointerId
      ) {
        return;
      }

      if (viewport.hasPointerCapture(event.pointerId)) {
        viewport.releasePointerCapture(event.pointerId);
      }

      resetDragging();
    };

    viewport.addEventListener('pointerup', stopDragging);
    viewport.addEventListener('pointercancel', stopDragging);

    viewport.addEventListener('lostpointercapture', () => {
      resetDragging();
    });

    /*
     * Escキーで閉じる
     */
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeLightbox();
    });

    /*
     * 閉じた後の状態を初期化
     */
    dialog.addEventListener('close', () => {
      dialog.classList.remove('is-zoomed');

      document.body.classList.remove(
        'article-lightbox-open'
      );

      modalImage.removeAttribute('src');
      modalImage.alt = '';

      didDrag = false;
      resetDragging();

      if (sourceImage) {
        sourceImage.focus({
          preventScroll: true
        });
      }

      sourceImage = null;
    });

    modalImage.addEventListener('load', () => {
      if (dialog.classList.contains('is-zoomed')) {
        centerZoomedImage();
      }
    });
  };

  /*
   * 拡大画面を開く
   */
  const openLightbox = (image) => {
    createLightbox();

    sourceImage = image;

    modalImage.src = getOriginalImageUrl(image);
    modalImage.alt = image.alt || '';

    dialog.classList.remove('is-zoomed');
    didDrag = false;
    resetDragging();

    dialog.showModal();

    document.body.classList.add(
      'article-lightbox-open'
    );

    closeButton.focus();
  };

  /*
   * ブログ本文画像を操作可能にする
   */
  const prepareImages = (root = document) => {
    root.querySelectorAll(IMAGE_SELECTOR).forEach((image) => {
      if (!image.closest('a')) {
        if (!image.hasAttribute('tabindex')) {
          image.setAttribute('tabindex', '0');
        }

        image.setAttribute('role', 'button');
        image.setAttribute(
          'aria-label',
          image.alt
            ? `${image.alt}を拡大表示`
            : '画像を拡大表示'
        );
      }
    });
  };

  /*
   * ブログ本文画像のクリック
   */
  document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) {
    return;
  }

  const image = event.target.closest(IMAGE_SELECTOR);

  if (!image) return;

  /* 画面幅749px以下では拡大機能を使用しない */
  if (window.matchMedia('(max-width: 749px)').matches) {
    return;
  }

  event.preventDefault();
  openLightbox(image);
});

  /*
   * Enterキーまたはスペースキーでも開ける
   */
  document.addEventListener('keydown', (event) => {
    if (!(event.target instanceof Element)) {
      return;
    }

    const image = event.target.matches(IMAGE_SELECTOR)
      ? event.target
      : null;

    if (
      !image ||
      (event.key !== 'Enter' && event.key !== ' ')
    ) {
      return;
    }
/* 画面幅749px以下ではキーボード操作でも拡大しない */
if (window.matchMedia('(max-width: 749px)').matches) {
  return;
}
    event.preventDefault();
    openLightbox(image);
  });

  /*
   * 初期設定
   */
  const initialize = () => {
    prepareImages();
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialize
    );
  } else {
    initialize();
  }

  /*
   * Shopifyテーマエディター内で
   * セクションが再読み込みされた場合
   */
  document.addEventListener(
    'shopify:section:load',
    (event) => {
      prepareImages(event.target);
    }
  );
})();